/**
 * `<OrgRoleSelect>` — inline org-role dropdown used by the org members table.
 *
 * The account-tier analogue of `<RoleSelect>`: org roles are a fixed, flat set
 * (Org Admin / Billing Admin / Member) with no custom roles, so the options are
 * static. Triggers `authClient.changeOrgMemberRole`; the server enforces
 * SELF_ROLE_CHANGE and LAST_ORG_ADMIN. The mutation invalidates the org-members
 * query so the cell reverts cleanly on failure.
 */

import { authClient, type AuthClientError, type OrgRole } from "@cometchat-ai/auth/client";
import { ORG_ROLES } from "@cometchat-ai/auth/shared";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from "@cometchat-ai/ui-dashboard";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { formatApiError } from "../lib/format-error";

export interface OrgRoleSelectProps {
  memberId: string;
  currentRole: OrgRole;
  disabled?: boolean;
}

export function OrgRoleSelect({ memberId, currentRole, disabled }: OrgRoleSelectProps) {
  const { t } = useTranslation("workspace");
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (role: OrgRole) => authClient.changeOrgMemberRole(memberId, role),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["org-members"] });
      toast.success(t("organization.members.changeRole.savedToast"));
    },
    onError: (err: AuthClientError) => {
      void queryClient.invalidateQueries({ queryKey: ["org-members"] });
      const description =
        err.code === "LAST_ORG_ADMIN"
          ? t("organization.members.changeRole.lastAdmin")
          : formatApiError(err);
      toast.error(t("organization.members.changeRole.failed"), { description });
    },
  });

  return (
    <Select
      value={currentRole}
      onValueChange={(value) => mutation.mutate(value as OrgRole)}
      disabled={disabled || mutation.isPending}
    >
      <SelectTrigger
        className="h-8 w-40 rounded-md text-[13px]"
        data-testid={`org-member-role-${memberId}`}
        aria-label={t("organization.members.table.role")}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {ORG_ROLES.map((role) => (
          <SelectItem
            key={role}
            value={role}
            data-testid={`org-member-role-option-${memberId}-${role}`}
          >
            {t(`organization.members.roles.${role}` as const)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
